import { Outlet } from "react-router-dom";
import { ChevronDown, LogOut } from "lucide-react";
import { Sidebar } from "./sidebar";
import { PlanNotice } from "@/components/plan-notice";
import { Button } from "@/components/ui/primitives";
import { useAuth } from "@/hooks/use-auth";
import { useI18n } from "@/i18n";
import type { LanguageCode } from "@/i18n/translations";

export function AppLayout() {
  const { email, organizations, activeOrg, activeOrgId, switchOrg, signOut } = useAuth();
  const { t, language, setLanguage } = useI18n();

  return (
    <div className="flex min-h-screen flex-col">
      <header className="flex items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex items-baseline gap-2">
          <span className="text-base font-bold tracking-tight">{t("appName")}</span>
          <span className="text-xs text-muted-foreground">{t("panelName")}</span>
        </div>

        <div className="ml-auto flex items-center gap-3">
          {organizations.length > 0 && (
            <div className="relative">
              <select
                value={activeOrgId ?? ""}
                onChange={(event) => switchOrg(event.target.value)}
                aria-label={t("switchOrganization")}
                className="h-9 appearance-none rounded-md border border-input bg-card pl-3 pr-8 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {organizations.map((org) => (
                  <option key={org.orgId} value={org.orgId}>
                    {org.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-2.5 size-4 text-muted-foreground" />
            </div>
          )}

          <select
            value={language}
            onChange={(event) => setLanguage(event.target.value as LanguageCode)}
            aria-label="Language"
            className="h-9 rounded-md border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="tr">TR</option>
            <option value="en">EN</option>
            <option value="zh">中文</option>
          </select>

          <span className="hidden text-sm text-muted-foreground sm:inline">{email}</span>
          <Button variant="ghost" size="sm" onClick={() => void signOut()}>
            <LogOut className="size-4" />
            <span className="hidden sm:inline">{t("signOut")}</span>
          </Button>
        </div>
      </header>

      <div className="flex flex-1 flex-col md:flex-row">
        <Sidebar />
        <main className="flex-1 p-4 md:p-6">
          {/* Above the outlet rather than inside each page: every page reads
              from the server, so every page is affected the same way. */}
          <PlanNotice plan={activeOrg?.plan} />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
